import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Storefront as StorefrontIcon } from '@mui/icons-material';

export type DealerLogoVariant = 'header' | 'avatar' | 'login' | 'profile' | 'small';

interface DealerLogoProps {
  dealershipId: string;
  dealershipName?: string;
  variant?: DealerLogoVariant;
  invertForDark?: boolean;
  alt?: string;
  showFallback?: boolean;
  /** Pre-fetched presigned URL from the backend (skips the separate API call) */
  preloadedUrl?: string | null;
  /** Cache-bust key - change after logo upload to force refresh */
  refreshKey?: string | number;
  sx?: object;
}

const variantSizes: Record<DealerLogoVariant, { height: number | string; maxWidth: number | string; avatarSize?: number; objectFit?: 'contain' | 'cover' }> = {
  header: { height: 40, maxWidth: 120, objectFit: 'contain' },
  avatar: { height: 32, maxWidth: 32, avatarSize: 32, objectFit: 'cover' },
  login: { height: 64, maxWidth: 200, objectFit: 'contain' },
  profile: { height: 80, maxWidth: 120, objectFit: 'contain' },
  small: { height: 24, maxWidth: 80, objectFit: 'contain' },
};

export const DealerLogo: React.FC<DealerLogoProps> = ({
  dealershipId,
  dealershipName = 'Dealership',
  variant = 'header',
  invertForDark = false,
  alt,
  showFallback = true,
  preloadedUrl,
  refreshKey,
  sx = {},
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(preloadedUrl || null);
  const [hasError, setHasError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sizes = variantSizes[variant];

  // Use preloadedUrl directly when it changes (from dealer-profile response)
  useEffect(() => {
    if (preloadedUrl) {
      setImageUrl(preloadedUrl);
      setHasError(false);
      setLoaded(false);
    }
  }, [preloadedUrl]);

  // Only fetch from API if no preloadedUrl was provided
  useEffect(() => {
    if (preloadedUrl) return;

    let cancelled = false;
    setImageUrl(null);
    setHasError(false);
    setLoaded(false);

    const apiUrl = `/.netlify/functions/dealer-logo?dealershipId=${encodeURIComponent(dealershipId)}&format=url`;

    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.presignedUrl) {
          setImageUrl(data.presignedUrl);
        } else {
          setHasError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [dealershipId, preloadedUrl, refreshKey]);

  if (hasError && !imageUrl && showFallback) {
    return (
      <StorefrontIcon
        sx={{
          ...(variant === 'avatar' ? { width: '100%', height: '100%', fontSize: sizes.avatarSize || 20 } : { height: sizes.height, width: 'auto' }),
          mr: variant !== 'avatar' ? 1.5 : 0,
          opacity: 0.9,
          ...sx,
        }}
      />
    );
  }

  if (!imageUrl) {
    return (
      <Box
        sx={{
          height: sizes.height,
          width: variant === 'avatar' ? sizes.height : 'auto',
          minWidth: variant === 'avatar' ? sizes.height : 0,
          mr: variant !== 'avatar' ? 1.5 : 0,
          bgcolor: 'action.hover',
          borderRadius: variant === 'avatar' ? '50%' : 1,
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={alt || `${dealershipName} logo`}
      onError={() => { setHasError(true); setImageUrl(null); }}
      onLoad={() => setLoaded(true)}
      sx={{
        height: sizes.height,
        maxWidth: sizes.maxWidth,
        width: variant === 'avatar' ? '100%' : 'auto',
        objectFit: sizes.objectFit || 'contain',
        ...(variant !== 'avatar' && { mr: 1.5 }),
        ...(invertForDark && { filter: 'brightness(0) invert(1)' }),
        opacity: loaded ? 1 : 0.7,
        transition: 'opacity 0.2s ease',
        ...sx,
      }}
    />
  );
};

export default DealerLogo;
